import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  FiUsers, FiUserCheck, FiPlus, FiTrash2, FiSave, FiSearch,
  FiChevronRight, FiInbox, FiRotateCcw, FiShield,
} from 'react-icons/fi';
import {
  tokens, PageShell, PageWidth, PageHeader,
  Card, CardHeader, CardTitle,
  Button, IconBtn, Input, SearchInput, Select, Badge, Tag, Checkbox,
  EmptyState, SideList, SideItem,
} from '../../../components/admin/AdminUI';

const c = tokens.color;
const API = '/request/perm';

const CAT_TITLE = {
  board: '게시판 관리', user: '사용자 관리', system: '시스템 관리',
  content: '컨텐츠 관리', file: '파일 관리', report: '리포트 관리',
};

const Tabs = styled.div`
  display: inline-flex;
  gap: 4px;
  background: ${c.surfaceMuted};
  border: 1px solid ${c.border};
  border-radius: ${tokens.radius.md};
  padding: 3px;
`;
const Tab = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  border: none; cursor: pointer;
  font-family: inherit; font-size: 14px; font-weight: 600;
  padding: 7px 16px; border-radius: ${tokens.radius.sm};
  background: ${(p) => (p.$active ? '#fff' : 'transparent')};
  color: ${(p) => (p.$active ? c.primary : c.muted)};
  box-shadow: ${(p) => (p.$active ? tokens.shadow.xs : 'none')};
  & svg { width: 15px; height: 15px; }
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 16px;
  align-items: stretch;
  min-height: calc(100vh - 230px);
  @media (max-width: 1024px) { grid-template-columns: 1fr; min-height: 0; }
`;
const Panel = styled(Card)`
  display: flex; flex-direction: column; min-height: 0;
  @media (max-width: 1024px) { min-height: 360px; }
`;
const Body = styled.div`
  display: flex; flex-direction: column; flex: 1; min-height: 0;
  padding: 14px 16px;
`;
const Scroll = styled.div`
  flex: 1; min-height: 80px; overflow-y: auto;
`;
const PermGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;
`;
const PermCard = styled.div`
  border: 1px solid ${c.border};
  border-radius: ${tokens.radius.md};
  overflow: hidden;
`;
const PermCardHead = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 12px; background: ${c.surfaceMuted};
  border-bottom: 1px solid ${c.border};
  font-size: 13px; font-weight: 700; color: ${c.heading};
`;
const PermRow = styled.label`
  display: flex; align-items: center; gap: 10px;
  padding: 9px 12px; cursor: pointer;
  border-bottom: 1px solid ${c.borderSubtle};
  font-size: 13px; color: ${c.text};
  &:last-child { border-bottom: none; }
  &:hover { background: ${c.surfaceMuted}; }
`;
const RowMain = styled.span` flex: 1; min-width: 0; `;
const Foot = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 12px 16px; border-top: 1px solid ${c.border};
`;
const AddRow = styled.div`
  display: flex; gap: 8px; padding-top: 12px; margin-top: 8px;
  border-top: 1px solid ${c.borderSubtle};
`;
const Dim = styled.span` color: ${c.subtle}; font-size: 12px; `;
const AcctName = styled.div` font-weight: 600; color: ${c.heading}; `;
const AcctSub = styled.div` font-size: 12px; color: ${c.muted}; margin-top: 2px; `;

const TINT = { board: 'brand', user: 'success', system: 'warning', content: 'brand', file: 'neutral', report: 'success' };

export default function OrganizationManagement() {
  const [tab, setTab] = useState('dept'); // 'dept' | 'account'
  const [catalog, setCatalog] = useState([]); // [{category, perm_key, label, description}]
  const [departments, setDepartments] = useState([]);

  // 부서별
  const [selDept, setSelDept] = useState(null);
  const [deptGrants, setDeptGrants] = useState({}); // perm_key -> bool
  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptParent, setNewDeptParent] = useState('');

  // 계정별
  const [acctSearch, setAcctSearch] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [selAcct, setSelAcct] = useState(null);
  const [acctPerms, setAcctPerms] = useState([]); // server rows
  const [overrides, setOverrides] = useState({}); // perm_key -> bool|null (pending)

  const cats = [...new Set(catalog.map((p) => p.category))];
  const byCat = (cat) => catalog.filter((p) => p.category === cat);

  // ---- 초기 로드 ----
  useEffect(() => {
    (async () => {
      try {
        const [cat, dep] = await Promise.all([
          fetch(`${API}/catalog`, { credentials: 'include' }).then((r) => r.json()),
          fetch(`${API}/departments`, { credentials: 'include' }).then((r) => r.json()),
        ]);
        if (Array.isArray(cat)) setCatalog(cat);
        if (Array.isArray(dep)) setDepartments(dep);
      } catch (e) { console.log(e); }
    })();
  }, []);

  // ---- 부서별 ----
  const loadDeptGrants = async (deptId) => {
    try {
      const m = await fetch(`${API}/department/grants?dept_id=${deptId}`, { credentials: 'include' }).then((r) => r.json());
      setDeptGrants(m && typeof m === 'object' ? m : {});
    } catch (e) { setDeptGrants({}); }
  };
  const selectDept = (d) => { setSelDept(d); loadDeptGrants(d.id); };
  const toggleDeptPerm = (key) => setDeptGrants((g) => ({ ...g, [key]: !g[key] }));
  const setCatAll = (cat, val) => {
    setDeptGrants((g) => { const n = { ...g }; byCat(cat).forEach((p) => { n[p.perm_key] = val; }); return n; });
  };
  const saveDeptGrants = async () => {
    if (!selDept) return;
    try {
      await fetch(`${API}/department/grants/save`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dept_id: selDept.id, grants: deptGrants }),
      });
      alert('부서 권한이 저장되었습니다.');
    } catch (e) { alert('저장 실패'); }
  };
  const addDept = async () => {
    if (!newDeptName.trim()) return;
    try {
      await fetch(`${API}/department/create`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newDeptName, parent_id: newDeptParent || null }),
      });
      setNewDeptName(''); setNewDeptParent('');
      const dep = await fetch(`${API}/departments`, { credentials: 'include' }).then((r) => r.json());
      setDepartments(dep);
    } catch (e) { alert('부서 추가 실패'); }
  };
  const delDept = async (d) => {
    if (!window.confirm(`'${d.name}' 부서를 삭제하시겠습니까?`)) return;
    try {
      await fetch(`${API}/department/delete`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: d.id }),
      });
      if (selDept && selDept.id === d.id) { setSelDept(null); setDeptGrants({}); }
      const dep = await fetch(`${API}/departments`, { credentials: 'include' }).then((r) => r.json());
      setDepartments(dep);
    } catch (e) { alert('삭제 실패'); }
  };

  // ---- 계정별 ----
  const loadAccounts = useCallback(async (search) => {
    try {
      const q = search ? `?search=${encodeURIComponent(search)}` : '';
      const rows = await fetch(`${API}/accounts${q}`, { credentials: 'include' }).then((r) => r.json());
      if (Array.isArray(rows)) setAccounts(rows);
    } catch (e) { setAccounts([]); }
  }, []);
  useEffect(() => { if (tab === 'account') loadAccounts(''); }, [tab, loadAccounts]);

  const selectAcct = async (a) => {
    setSelAcct(a); setOverrides({});
    try {
      const rows = await fetch(`${API}/account/permissions?user_id=${encodeURIComponent(a.user_id)}`, { credentials: 'include' }).then((r) => r.json());
      setAcctPerms(Array.isArray(rows) ? rows : []);
    } catch (e) { setAcctPerms([]); }
  };
  const acctRow = (key) => acctPerms.find((r) => r.perm_key === key) || {};
  const displayedEff = (key) => {
    if (key in overrides) { const v = overrides[key]; return v === null ? (acctRow(key).dept_granted || false) : v; }
    return !!acctRow(key).effective;
  };
  const isOverridden = (key) => {
    if (key in overrides) return overrides[key] !== null;
    return acctRow(key).user_granted !== null && acctRow(key).user_granted !== undefined;
  };
  const toggleAcctPerm = (key) => setOverrides((o) => ({ ...o, [key]: !displayedEff(key) }));
  const clearOverride = (key) => setOverrides((o) => ({ ...o, [key]: null }));
  const changeAcctDept = async (deptId) => {
    if (!selAcct) return;
    try {
      await fetch(`${API}/account/dept`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: selAcct.user_id, dept_id: deptId || null }),
      });
      const a = { ...selAcct, perm_dept_id: deptId || null };
      setSelAcct(a);
      loadAccounts(acctSearch);
      const rows = await fetch(`${API}/account/permissions?user_id=${encodeURIComponent(a.user_id)}`, { credentials: 'include' }).then((r) => r.json());
      setAcctPerms(Array.isArray(rows) ? rows : []);
      setOverrides({});
    } catch (e) { alert('부서 변경 실패'); }
  };
  const saveAcctOverrides = async () => {
    if (!selAcct) return;
    const payload = Object.entries(overrides).map(([perm_key, granted]) => ({ perm_key, granted }));
    if (!payload.length) { alert('변경사항이 없습니다.'); return; }
    try {
      await fetch(`${API}/account/override`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: selAcct.user_id, overrides: payload }),
      });
      const rows = await fetch(`${API}/account/permissions?user_id=${encodeURIComponent(selAcct.user_id)}`, { credentials: 'include' }).then((r) => r.json());
      setAcctPerms(Array.isArray(rows) ? rows : []);
      setOverrides({});
      alert('계정 권한이 저장되었습니다.');
    } catch (e) { alert('저장 실패'); }
  };

  const deptOptions = [{ value: '', label: '소속 없음' }, ...departments.map((d) => ({ value: String(d.id), label: d.name }))];

  return (
    <PageShell>
      <PageWidth>
        <PageHeader
          title="권한관리"
          subtitle="부서별 통합 권한과 계정별 권한을 조회하고 설정합니다."
          actions={
            <Tabs>
              <Tab $active={tab === 'dept'} onClick={() => setTab('dept')}><FiUsers /> 부서별 권한</Tab>
              <Tab $active={tab === 'account'} onClick={() => setTab('account')}><FiUserCheck /> 계정별 권한</Tab>
            </Tabs>
          }
        />

        {tab === 'dept' ? (
          <Grid>
            {/* 부서 목록 */}
            <Panel $pad={false}>
              <CardHeader>
                <CardTitle><FiUsers /> 부서</CardTitle>
                <Badge $tone="neutral">{departments.length}</Badge>
              </CardHeader>
              <Body>
                <Scroll>
                  {departments.length === 0 ? (
                    <EmptyState icon={<FiInbox />} title="부서가 없습니다" pad="32px 16px" />
                  ) : (
                    <SideList>
                      {departments.map((d) => (
                        <SideItem key={d.id} $active={selDept && selDept.id === d.id} onClick={() => selectDept(d)}
                          style={{ paddingLeft: 12 + d.level * 14 }}>
                          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <span>{d.name}</span>
                            <Dim>{d.description}</Dim>
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Badge $tone="neutral">{d.member_count}명</Badge>
                            <IconBtn $size="sm" title="삭제" onClick={(e) => { e.stopPropagation(); delDept(d); }}><FiTrash2 /></IconBtn>
                          </span>
                        </SideItem>
                      ))}
                    </SideList>
                  )}
                </Scroll>
                <AddRow>
                  <Input placeholder="새 부서명" value={newDeptName} onChange={(e) => setNewDeptName(e.target.value)} />
                  <Select value={newDeptParent} onChange={setNewDeptParent} options={deptOptions} minWidth="120px" placeholder="상위" />
                  <Button $variant="primary" onClick={addDept}><FiPlus /></Button>
                </AddRow>
              </Body>
            </Panel>

            {/* 부서 권한 매트릭스 */}
            <Panel $pad={false}>
              <CardHeader>
                <CardTitle><FiShield /> {selDept ? `${selDept.name} 권한` : '권한 설정'}</CardTitle>
                {selDept && <Button $variant="primary" $size="sm" onClick={saveDeptGrants}><FiSave /> 저장</Button>}
              </CardHeader>
              <Body>
                {!selDept ? (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EmptyState icon={<FiShield />} title="부서를 선택하세요" description="좌측에서 부서를 선택하면 권한을 설정할 수 있습니다." />
                  </div>
                ) : (
                  <Scroll>
                    <PermGrid>
                      {cats.map((cat) => (
                        <PermCard key={cat}>
                          <PermCardHead>
                            <span>{CAT_TITLE[cat] || cat}</span>
                            <span style={{ display: 'flex', gap: 4 }}>
                              <Button $variant="ghost" $size="sm" onClick={() => setCatAll(cat, true)}>전체</Button>
                              <Button $variant="ghost" $size="sm" onClick={() => setCatAll(cat, false)}>해제</Button>
                            </span>
                          </PermCardHead>
                          {byCat(cat).map((p) => (
                            <PermRow key={p.perm_key}>
                              <Checkbox checked={!!deptGrants[p.perm_key]} onChange={() => toggleDeptPerm(p.perm_key)} />
                              <RowMain>{p.label}</RowMain>
                            </PermRow>
                          ))}
                        </PermCard>
                      ))}
                    </PermGrid>
                  </Scroll>
                )}
              </Body>
            </Panel>
          </Grid>
        ) : (
          <Grid>
            {/* 계정 목록 */}
            <Panel $pad={false}>
              <CardHeader>
                <CardTitle><FiUsers /> 계정</CardTitle>
                <Badge $tone="neutral">{accounts.length}</Badge>
              </CardHeader>
              <Body>
                <SearchInput placeholder="아이디/이름 검색" value={acctSearch}
                  onChange={(e) => setAcctSearch(e.target.value)} onSearch={() => loadAccounts(acctSearch)} grow="0 0 auto" />
                <Scroll style={{ marginTop: 12 }}>
                  {accounts.length === 0 ? (
                    <EmptyState icon={<FiInbox />} title="계정이 없습니다" pad="32px 16px" />
                  ) : (
                    <SideList>
                      {accounts.map((a) => (
                        <SideItem key={a.user_id} $active={selAcct && selAcct.user_id === a.user_id}
                          onClick={() => selectAcct(a)} style={{ alignItems: 'flex-start' }}>
                          <span style={{ minWidth: 0 }}>
                            <AcctName>{a.user_name || a.user_id}</AcctName>
                            <AcctSub>{a.user_id}{a.user_rank ? ` · ${a.user_rank}` : ''}</AcctSub>
                          </span>
                          {a.dept_name ? <Badge $tone="brand">{a.dept_name}</Badge> : <Dim>미배정</Dim>}
                        </SideItem>
                      ))}
                    </SideList>
                  )}
                </Scroll>
              </Body>
            </Panel>

            {/* 계정 권한 */}
            <Panel $pad={false}>
              <CardHeader>
                <CardTitle><FiUserCheck /> {selAcct ? (selAcct.user_name || selAcct.user_id) : '계정 권한'}</CardTitle>
                {selAcct && <Button $variant="primary" $size="sm" onClick={saveAcctOverrides}><FiSave /> 저장</Button>}
              </CardHeader>
              <Body>
                {!selAcct ? (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EmptyState icon={<FiUserCheck />} title="계정을 선택하세요" description="좌측에서 계정을 선택하면 실효 권한을 조회/편집할 수 있습니다." />
                  </div>
                ) : (
                  <Scroll>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, color: c.muted }}>소속 부서</span>
                      <Select value={selAcct.perm_dept_id ? String(selAcct.perm_dept_id) : ''} onChange={changeAcctDept} options={deptOptions} minWidth="160px" />
                      <Dim>부서 권한을 상속받고, 개별 항목만 예외 조정됩니다.</Dim>
                    </div>
                    <PermGrid>
                      {cats.map((cat) => (
                        <PermCard key={cat}>
                          <PermCardHead><span>{CAT_TITLE[cat] || cat}</span></PermCardHead>
                          {byCat(cat).map((p) => (
                            <PermRow key={p.perm_key}>
                              <Checkbox checked={displayedEff(p.perm_key)} onChange={() => toggleAcctPerm(p.perm_key)} />
                              <RowMain>{p.label}</RowMain>
                              {isOverridden(p.perm_key) ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  <Badge $tone="warning">개별</Badge>
                                  <IconBtn $size="sm" title="부서값으로 초기화" onClick={() => clearOverride(p.perm_key)}><FiRotateCcw /></IconBtn>
                                </span>
                              ) : (
                                <Tag>상속</Tag>
                              )}
                            </PermRow>
                          ))}
                        </PermCard>
                      ))}
                    </PermGrid>
                  </Scroll>
                )}
              </Body>
            </Panel>
          </Grid>
        )}
      </PageWidth>
    </PageShell>
  );
}
